import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'
import { SearchIcon } from './icons'
import styles from './SearchField.module.css'

/**
 * Props for {@link SearchField}.
 */
export interface SearchFieldProps {
  /** Controlled text value. */
  readonly value: string
  /** Placeholder shown when empty. */
  readonly placeholder: string
  /** Called with the new text on every input change and on clear. */
  readonly onChange: (value: string) => void
  /** Accessible label for the clear button. @defaultValue `'Clear search'` */
  readonly clearLabel?: string
}

/**
 * Search text input for the Suggester header.
 *
 * - Controlled — `value` / `onChange` are owned by the host.
 * - Autofocuses on mount (spec §3 — "autofocus on open").
 * - Renders a clear button only while the value is non-empty; clicking
 *   it emits `onChange('')`.
 * - Carries the spec a11y id `suggester-search-field`.
 */
export function SearchField({
  value,
  placeholder,
  onChange,
  clearLabel = 'Clear search',
}: SearchFieldProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null)

  // Autofocus on open. `autoFocus` JSX attribute is unreliable inside
  // overlays that mount after a transition — an explicit imperative
  // focus on mount is deterministic and testable.
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <div className={styles.field}>
      <span className={styles.searchIcon}>
        <SearchIcon size={16} />
      </span>
      <input
        ref={inputRef}
        type="search"
        className={styles.input}
        data-testid="suggester-search-field"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // Native `type="search"` shows a UA clear affordance on some
        // browsers; we render our own for consistent styling. Suppress
        // autocorrect noise on station names.
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {value.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          data-testid="suggester-search-clear"
          aria-label={clearLabel}
          onClick={() => onChange('')}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" aria-hidden focusable="false" fill="none">
            <path
              d="M2.5 2.5 L8.5 8.5 M8.5 2.5 L2.5 8.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
